import { runTossAppLogin } from '../hooks/useTossAppLogin';
import { clearLoginMark, saveLoginSession } from '../utils/loginSession';
import { isTossApp } from '../utils/env';
import { Cafe, OwnerCafeAttributes, CafeSearchPage, NearbyNaverCafe, SeatStatusType, UserLoginResponse, OwnerRegisterData, OwnerDevRegisterData, PendingOwner, AddressSearchResult, CafeImage, WeeklySchedule, ResolvedCafe, ResolveCafeInput, FavoriteCafe, FavoriteSort, FavoriteToggleResult, ShareTarget, FavoriteDashboard, BlogReview, CafeSearchItem } from '../types/cafe';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';

const TOSS_LOGIN_PATH = '/users/toss-login';

/** JWT 의 exp(만료 시각, 초)를 읽는다. 서명 검증은 서버 몫이고, 여기선 만료 여부만 본다. */
function isTokenExpired(token: string): boolean {
  try {
    const payload = (token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload)) as { exp?: number };
    // 요청이 가는 사이 만료되는 경우를 피하려고 30초 여유를 둔다.
    return typeof exp === 'number' && exp * 1000 < Date.now() + 30_000;
  } catch {
    return false;
  }
}

let reloginInFlight: Promise<boolean> | null = null;

/**
 * 만료된 토큰을 토스 appLogin 으로 조용히 갱신한다. 동시에 여러 요청이 와도 한 번만 로그인한다.
 * 이미 동의한 사용자는 토스 창 없이 인가코드가 온다(앱인토스 문서).
 * 실패하면(토스 '연결 끊기' 등) 세션을 비우고 인트로로 보낸다.
 */
function silentRelogin(): Promise<boolean> {
  if (!reloginInFlight) {
    reloginInFlight = (async () => {
      try {
        const { authorizationCode, referrer } = await runTossAppLogin();
        saveLoginSession(await tossLogin(authorizationCode, referrer));
        return true;
      } catch (err) {
        console.error('[TossLogin] silent relogin failed:', err);
        clearLoginMark();
        ['user_token', 'user_id', 'user_name', 'owner_token', 'owner_cafe_id', 'owner_cafe_name']
          .forEach((k) => sessionStorage.removeItem(k));
        window.location.replace('/');
        return false;
      } finally {
        reloginInFlight = null;
      }
    })();
  }
  return reloginInFlight;
}

/**
 * 보낼 토큰이 토스 로그인으로 받은 것(user/owner)이고 만료됐으면 재로그인 후 새 토큰을 돌려준다.
 * 토스 앱 밖(로컬 개발)이나 관리자 토큰은 그대로 둔다.
 * request() 를 거치지 않는 실시간 연결(EventSource)도 연결 직전에 이걸 부른다.
 */
export async function freshToken(token: string): Promise<string> {
  const kind = token === sessionStorage.getItem('user_token') ? 'user_token'
    : token === sessionStorage.getItem('owner_token') ? 'owner_token'
    : null;
  if (!kind || !isTossApp() || !isTokenExpired(token)) return token;
  if (!(await silentRelogin())) return token;
  return sessionStorage.getItem(kind) ?? token;
}

/**
 * 서버 응답 상태코드를 보존하는 에러.
 * 찜/공유는 403(비로그인)·409(폐업)를 서로 다른 토스트로 안내해야 해서 status 가 필요하다.
 */
export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const { headers: optionHeaders, ...restOptions } = options;
  const mergedHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(optionHeaders as Record<string, string>),
  };

  // V9 is_test viewer-aware 응답을 위해 로그인 상태면 기본 Authorization 헤더 자동 첨부.
  // 호출자가 options.headers.Authorization 을 이미 지정했으면 그 값 유지.
  // 우선순위는 useSeatStatusSSE 와 동일 (user → owner → admin).
  if (!mergedHeaders['Authorization'] && !mergedHeaders['authorization']
      && typeof sessionStorage !== 'undefined') {
    const token = sessionStorage.getItem('user_token')
      || sessionStorage.getItem('owner_token')
      || sessionStorage.getItem('admin_token');
    if (token) {
      mergedHeaders['Authorization'] = `Bearer ${token}`;
    }
  }

  // 토큰 만료 시 서버는 401 이 아닌 403 을 준다(권한 없음과 구분 불가).
  // 그래서 응답을 보고 재시도하지 않고, 보내기 전에 만료 여부를 확인해 갱신한다.
  const auth = mergedHeaders['Authorization'] ?? mergedHeaders['authorization'];
  if (auth?.startsWith('Bearer ') && path !== TOSS_LOGIN_PATH && typeof sessionStorage !== 'undefined') {
    const token = auth.slice('Bearer '.length);
    const renewed = await freshToken(token);
    if (renewed !== token) {
      delete mergedHeaders['authorization'];
      mergedHeaders['Authorization'] = `Bearer ${renewed}`;
    }
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...restOptions,
    headers: mergedHeaders,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const message = body?.message || `API error ${response.status}: ${response.statusText}`;
    throw new ApiError(message, response.status);
  }

  const text = await response.text();
  return text ? JSON.parse(text) as T : (undefined as T);
}

// --- 찜 · 공유 (docs/08-찜-공유-검색-API계약.md) ---

/**
 * 카카오에서만 온 카페(cafeId=null)를 내부 cafeId로 해석한다.
 * 멱등이라 재호출해도 같은 cafeId 가 오지만 왕복이 낭비되므로,
 * 결과 cafeId 는 호출한 쪽에서 목록 아이템에 반영해 둘 것.
 * 찜/공유를 실제로 누른 항목에 대해서만 lazy 하게 호출한다(목록 전체 선호출 금지).
 */
export async function resolveCafe(input: ResolveCafeInput): Promise<ResolvedCafe> {
  return request<ResolvedCafe>('/cafes/resolve', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** 찜하기. 멱등이라 더블탭 방어 불필요. */
export async function addFavorite(cafeId: number): Promise<FavoriteToggleResult> {
  return request<FavoriteToggleResult>(`/cafes/${cafeId}/favorite`, { method: 'POST' });
}

/** 찜 해제. 멱등이며 폐업 카페도 해제 가능(목록에서 치우려는 요청을 막지 않는다). */
export async function removeFavorite(cafeId: number): Promise<void> {
  return request<void>(`/cafes/${cafeId}/favorite`, { method: 'DELETE' });
}

/** 내 찜 목록. 좌표를 주면 distanceMeters 가 채워지고 sort=distance 를 쓸 수 있다. */
export async function fetchFavorites(options: {
  lat?: number;
  lng?: number;
  sort?: FavoriteSort;
} = {}): Promise<FavoriteCafe[]> {
  const params = new URLSearchParams();
  if (options.lat !== undefined && options.lng !== undefined) {
    params.set('lat', String(options.lat));
    params.set('lng', String(options.lng));
  }
  if (options.sort) params.set('sort', options.sort);
  const qs = params.toString();
  return request<FavoriteCafe[]>(`/favorites${qs ? `?${qs}` : ''}`);
}

/** 리스트 하트 상태 판정용 경량 조회. 진입 시 1회 부르고 Set 으로 들고 있을 것. */
export async function fetchFavoriteIds(): Promise<number[]> {
  return request<number[]>('/favorites/ids');
}

/** 공유 메타데이터. 409(폐업)면 공유 불가. */
export async function fetchShareTarget(cafeId: number): Promise<ShareTarget> {
  return request<ShareTarget>(`/cafes/${cafeId}/share-target`);
}

/**
 * 공유 시도 집계. fire-and-forget — 실패해도 공유 UX 를 막지 않는다.
 * 의도적으로 Promise 를 반환하지 않는다(호출부에서 await 하지 말 것).
 */
export function postShareEvent(cafeId: number, channel: string = 'toss'): void {
  void request<void>(`/cafes/${cafeId}/share-events`, {
    method: 'POST',
    body: JSON.stringify({ channel }),
  }).catch(() => { /* 집계 실패는 무시 */ });
}

/**
 * 사장님 찜 지표.
 * 토큰을 반드시 명시한다 — request 의 자동 선택(user→owner→admin)에 맡기면
 * 사장님 계정에서 USER 토큰이 전송돼 403 이 난다.
 */
export async function fetchFavoriteDashboard(
  cafeId: number,
  token: string
): Promise<FavoriteDashboard> {
  return request<FavoriteDashboard>(`/cafes/${cafeId}/dashboard/favorites`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

// --- Cafe ---

export async function fetchNearbyCafes(
  lat: number,
  lng: number,
  radius: number = 1000
): Promise<Cafe[]> {
  const params = new URLSearchParams({
    lat: String(lat),
    lng: String(lng),
    radius: String(radius),
  });
  return request<Cafe[]>(`/cafes/nearby?${params.toString()}`);
}

/** 현재(KST) 영업 중인 주변 카페만 조회. UI 통합은 후속 PR 예정. */
export async function fetchCafesOpenNow(
  lat: number,
  lng: number,
  radius: number = 1000
): Promise<Cafe[]> {
  return request<Cafe[]>(
    `/cafes/open-now?lat=${lat}&lng=${lng}&radius=${radius}`
  );
}

export async function fetchNearbyCafesNaver(
  lat: number,
  lng: number,
  radius: number = 1000
): Promise<NearbyNaverCafe[]> {
  const params = new URLSearchParams({
    lat: String(lat),
    lng: String(lng),
    radius: String(radius),
  });
  return request<NearbyNaverCafe[]>(`/cafes/nearby-cafes?${params.toString()}`);
}

/**
 * 카페 검색 (DB + 카카오 키워드 병합).
 *
 * 응답은 `CafeSearchItem[]` 다 — 예전 타입(`Cafe[]`)은 실제 응답과 맞지 않았다.
 * 서버는 아직 페이징이 없어 한 번에 최대 45건을 준다. 페이징이 붙으면
 * `{ items, hasNext }` 로 바뀌므로 호출부에서 그때 맞춘다(백엔드 인계 문서 §4-3).
 */
export async function searchCafes(
  keyword: string,
  opts?: { lat?: number; lng?: number; size?: number }
): Promise<CafeSearchItem[]> {
  const params = new URLSearchParams({ keyword });
  if (opts?.lat != null && opts?.lng != null) {
    params.set('lat', String(opts.lat));
    params.set('lng', String(opts.lng));
  }
  if (opts?.size != null) params.set('size', String(opts.size));
  return request<CafeSearchItem[]>(`/cafes/search?${params.toString()}`);
}

/** 페이지 검색. page는 0부터 시작하며 후속 페이지는 첫 응답의 snapshotId를 전달한다. */
export async function searchCafesPage(
  keyword: string,
  opts: { lat?: number; lng?: number; page: number; size?: number; snapshotId?: string }
): Promise<CafeSearchPage> {
  const params = new URLSearchParams({ keyword, page: String(opts.page), size: String(opts.size ?? 20) });
  if (opts.lat != null && opts.lng != null) {
    params.set('lat', String(opts.lat));
    params.set('lng', String(opts.lng));
  }
  if (opts.snapshotId) params.set('snapshotId', opts.snapshotId);
  return request<CafeSearchPage>(`/cafes/search?${params}`);
}

export async function fetchCafeDetail(id: number): Promise<Cafe> {
  return request<Cafe>(`/cafes/${id}`);
}

export async function updateSeatStatus(
  cafeId: number,
  status: SeatStatusType,
  token: string
): Promise<void> {
  await request<void>(`/cafes/${cafeId}/seat-status`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ status }),
  });
}

export async function importCafe(
  req: { name: string; address: string; latitude: number; longitude: number; phone?: string }
): Promise<Cafe> {
  return request<Cafe>('/cafes/import', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

// --- Owner ---

/**
 * 사업자등록증을 비공개 S3 버킷에 업로드하고 S3 key를 반환한다.
 * 미리보기는 호출자가 `URL.createObjectURL(file)`로 클라이언트 blob을 사용한다 (서버 왕복 불필요).
 */
export async function uploadOwnerDocument(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BASE_URL}/owners/register/document`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message || '파일 업로드 실패');
  }
  const data = await res.json();
  return data.key;
}

/**
 * Owner OAuth 로그인 (plan-eng-review 2026-04-19 — 평문 tossUserKey 폐기).
 *
 * 토스 SDK appLogin() 으로 획득한 authorizationCode + referrer 를 서버에 전달하면,
 * 서버가 TossAuthService.exchangeToken 으로 tossUserKey 를 검증 후 JWT 를 반환한다.
 */
export async function ownerLogin(
  authorizationCode: string,
  referrer: string
): Promise<{ token: string; cafeId: number; name: string; cafeName: string }> {
  return request<{ token: string; cafeId: number; name: string; cafeName: string }>(
    '/owners/login',
    {
      method: 'POST',
      body: JSON.stringify({ authorizationCode, referrer }),
    }
  );
}

export async function ownerRegister(data: OwnerRegisterData): Promise<{ ownerId: number; status: string; message: string }> {
  return request<{ ownerId: number; status: string; message: string }>(
    '/owners/register',
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

export async function ownerRegisterDev(data: OwnerDevRegisterData): Promise<{ ownerId: number; status: string; message: string }> {
  return request<{ ownerId: number; status: string; message: string }>(
    '/owners/register-dev',
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

// --- Address ---

export async function searchAddress(
  query: string,
  page: number = 1
): Promise<AddressSearchResult> {
  return request<AddressSearchResult>(
    `/address/search?query=${encodeURIComponent(query)}&page=${page}`
  );
}

// --- Business Number ---

export async function verifyBusinessNumber(
  businessNumber: string
): Promise<{ valid: boolean; businessStatus?: string; message?: string }> {
  return request<{ valid: boolean; businessStatus?: string; message?: string }>(
    '/owners/verify-business-number',
    {
      method: 'POST',
      body: JSON.stringify({ businessNumber }),
    }
  );
}

// --- Cafe Images ---

export async function uploadCafeImage(
  cafeId: number,
  file: File,
  token: string
): Promise<{ id: number; url: string; order: number }> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BASE_URL}/cafes/${cafeId}/images`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message || '이미지 업로드 실패');
  }
  return res.json();
}

export async function fetchCafeImages(cafeId: number): Promise<CafeImage[]> {
  return request<CafeImage[]>(`/cafes/${cafeId}/images`);
}

export async function deleteCafeImage(
  cafeId: number,
  imageId: number,
  token: string
): Promise<void> {
  await request<void>(`/cafes/${cafeId}/images/${imageId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
}

// --- Notification ---

export interface NotificationHistoryItem {
  registered: boolean;
  id: number;
  cafeId: number;
  cafeName: string;
  seatCapacity: string | null;
  notifiedCount: number;
  sentAt: string;
  // 알림 바텀시트의 거리(도보)·주소 표기 및 카페 이동(지도 recenter)용. 등록 카페만 발송되므로 항상 존재.
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  addressDetail: string | null;
}

export async function fetchRecentNotifications(): Promise<NotificationHistoryItem[]> {
  return request<NotificationHistoryItem[]>('/notifications/recent');
}

export async function notifyAvailability(
  cafeId: number,
  token: string,
  seatCapacity?: string
): Promise<{ notifiedCount: number; expiresAt: string }> {
  return request<{ notifiedCount: number; expiresAt: string }>(
    `/cafes/${cafeId}/notify-available`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ seatCapacity }),
    }
  );
}

// --- Cafe Update ---

export async function updateCafe(
  cafeId: number,
  data: { name: string; address: string; addressDetail?: string; phone?: string; openHours?: WeeklySchedule | null; instagramHandle?: string },
  token: string
): Promise<Cafe> {
  return request<Cafe>(`/cafes/${cafeId}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
}

// --- Staff ---

export interface StaffPermissions {
  canManageSeat: boolean;
  canEditCafeInfo: boolean;
  canSendNotification: boolean;
}

export interface StaffMember {
  id: number;
  name: string;
  tossUserKey: string;
  status: string;
  canManageSeat?: boolean;
  canEditCafeInfo?: boolean;
  canSendNotification?: boolean;
}

export async function fetchStaffList(cafeId: number, token: string): Promise<StaffMember[]> {
  return request<StaffMember[]>(`/cafes/${cafeId}/staff`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function inviteStaff(
  cafeId: number,
  tossUserKey: string,
  name: string,
  token: string
): Promise<StaffMember> {
  return request<StaffMember>(`/cafes/${cafeId}/staff`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ tossUserKey, name }),
  });
}

export async function removeStaff(
  cafeId: number,
  staffId: number,
  token: string
): Promise<void> {
  await request<void>(`/cafes/${cafeId}/staff/${staffId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function updateStaffPermissions(
  cafeId: number,
  staffId: number,
  permissions: Partial<StaffPermissions>,
  token: string
): Promise<{ staffId: number; name: string } & StaffPermissions> {
  return request(`/cafes/${cafeId}/staff/${staffId}/permissions`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(permissions),
  });
}

// --- Cafe Attributes ---

export async function fetchCafeAttributes(
  cafeId: number,
  token: string
): Promise<OwnerCafeAttributes> {
  return request<OwnerCafeAttributes>(`/cafes/${cafeId}/attributes`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function updateCafeAttributes(
  cafeId: number,
  attributes: OwnerCafeAttributes,
  token: string
): Promise<void> {
  await request<void>(`/cafes/${cafeId}/attributes`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(attributes),
  });
}

// --- Cafe Availability (V8 재설계: 활성 홍보 상태) ---

/** 현재 활성 중인 빈자리 홍보 1건. 오너 UI 에서 "홍보 중단" 버튼 분기용. */
export interface ActiveAvailability {
  seatCapacity: string;
  startedAt: string;
  expiresAt: string;
  notificationId: number;
}

export async function fetchActiveAvailabilities(cafeId: number): Promise<ActiveAvailability[]> {
  return request<ActiveAvailability[]>(`/cafes/${cafeId}/notifications/active`);
}

/**
 * 활성 홍보 중단. DELETE /cafes/{id}/notifications/active?seatCapacity=X.
 * 활성 레코드 없으면 404 (멱등 DELETE 이지만 응답은 404) — 호출자가 상태 재동기화해야 함.
 */
export async function cancelAvailability(
  cafeId: number,
  seatCapacity: string,
  token: string
): Promise<void> {
  await request<void>(
    `/cafes/${cafeId}/notifications/active?seatCapacity=${encodeURIComponent(seatCapacity)}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

// --- User ---

export async function userLogin(tossUserKey: string): Promise<UserLoginResponse> {
  return request<UserLoginResponse>(
    '/users/login',
    {
      method: 'POST',
      body: JSON.stringify({ tossUserKey }),
    }
  );
}

export async function tossLogin(authorizationCode: string, referrer: string): Promise<UserLoginResponse> {
  return request<UserLoginResponse>(
    TOSS_LOGIN_PATH,
    {
      method: 'POST',
      body: JSON.stringify({ authorizationCode, referrer }),
    }
  );
}

export async function updateLocationConsent(consent: boolean, token: string): Promise<void> {
  await request<void>('/users/location-consent', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ consent }),
  });
}

export async function updateUserLocation(
  latitude: number,
  longitude: number,
  token: string
): Promise<void> {
  const res = await fetch(`${BASE_URL}/users/location`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ latitude, longitude }),
  });
  if (!res.ok) {
    throw new Error(`updateUserLocation failed: ${res.status}`);
  }
}

/** ownerStatus: 사장님 등록 상태. owners 에 없으면 'NONE' — 입점 진입점은 NONE 일 때만 노출한다. */
export type OwnerStatus = 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';

export async function getUserMe(): Promise<{ searching: boolean; ownerStatus: OwnerStatus }> {
  return request<{ searching: boolean; ownerStatus: OwnerStatus }>('/users/me');
}

export async function updateSearching(searching: boolean, token: string): Promise<void> {
  await request<void>('/users/searching', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ searching }),
  });
}

export async function updateNotificationConsent(consent: boolean, token: string): Promise<void> {
  await request<void>('/users/notification-consent', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ consent }),
  });
}

/**
 * 선호 좌석 용량(chip↔push 의미 일치)을 서버에 동기화.
 * 빈 배열 전송 시 서버에서 NULL로 저장되어 "모든 용량 수신"(기본값)으로 복귀.
 */
export async function updateUserPreferredSeatCapacities(
  seatCapacities: string[],
  token: string
): Promise<void> {
  await request<void>('/users/preferences/seat-capacities', {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ seatCapacities }),
  });
}

// --- Admin ---

export async function adminLogin(username: string, password: string): Promise<{ token: string }> {
  return request<{ token: string }>(
    '/admin/login',
    {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }
  );
}

export async function fetchPendingOwners(token: string): Promise<PendingOwner[]> {
  return request<PendingOwner[]>('/admin/owners/pending', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function approveOwner(id: number, token: string): Promise<void> {
  await request<void>(`/admin/owners/${id}/approve`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function rejectOwner(id: number, token: string): Promise<void> {
  await request<void>(`/admin/owners/${id}/reject`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * 어드민 심사용 사업자등록증 presigned URL (5분 만료) 발급.
 */
export async function fetchOwnerDocumentUrl(id: number, token: string): Promise<string> {
  const data = await request<{ url: string }>(`/admin/owners/${id}/document`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data.url;
}

/**
 * 카페 블로그 후기 조회 (네이버 블로그 검색 결과).
 * 미등록 카페는 cafeId 가 없어 이름·주소로 찾는다. 서버 API 미구현 상태에선 실패하며, 호출부가 섹션을 숨긴다.
 */
export async function fetchBlogReviews(
  name: string,
  address: string | null,
  size = 10
): Promise<BlogReview[]> {
  const params = new URLSearchParams({ name, size: String(size) });
  if (address) params.set('address', address);
  return request<BlogReview[]>(`/cafes/blog-reviews?${params.toString()}`);
}
